import React, { useState, useEffect, useContext } from 'react';
import { useRange } from 'react-instantsearch';

import DoubleRangeSlider from '../double-range-slider/DoubleRangeSlider';
import SaleItems from './SaleItems';
import GtmContext from '../../contexts/GtmContext';
import PriceRangeDropdownShell from './PriceRangeDropdownShell';

/**
 * Inner slider — reads min/max from Algolia's useRange hook
 */
const PriceRangeSlider = ({ setCurrentRefinement, nbHits, setPriceRange, showDropdown, setRangeValues }) => {
  const { refine, range, start } = useRange({ attribute: 'facets.Price' });
  const { min, max } = range;
  const currentRefinement = {
    min: Math.max(min, Number.isFinite(start[0]) ? start[0] : min),
    max: Math.min(max, Number.isFinite(start[1]) ? start[1] : max),
  };

  // Reset price range slider when min/max values change (on new search term entered for example)
  useEffect(() => {
    setRangeValues?.({ min, max });
    refine([min, max]);
    if (min !== currentRefinement.min || max !== currentRefinement.max) {
      setPriceRange({ min, max });
    }
  }, [min, max]);

  useEffect(() => {
    if (
      currentRefinement.isRefined !==
      (currentRefinement && (currentRefinement.min !== min || currentRefinement.max !== max))
    ) {
      setCurrentRefinement({
        ...currentRefinement,
        isRefined: currentRefinement && (currentRefinement.min !== min || currentRefinement.max !== max),
      });
    }
  }, [currentRefinement.max, currentRefinement.min]);

  const onRefine = ({ currentMin, currentMax }) => {
    refine([currentMin, currentMax]);
    setPriceRange({ min: currentMin, max: currentMax });
  };

  return (
    <DoubleRangeSlider
      showDollar={true}
      nbHits={nbHits}
      showDropdown={showDropdown}
      min={min}
      max={max}
      currentMin={currentRefinement.min}
      currentMax={currentRefinement.max || 0}
      onRefine={onRefine}
    />
  );
};

/**
 * Algolia price range dropdown — uses PriceRangeDropdownShell for the shared UI
 */
const PriceRangeContainer = ({ setPriceRange, setSaleItems, nbHits, nbSaleItems, setRangeValues, searchTerm }) => {
  const [currentRefinement, setCurrentRefinement] = useState({ isRefined: false });
  const { gtm, setGtm } = useContext(GtmContext);

  useEffect(() => {
    if (currentRefinement.isRefined && (currentRefinement.min || currentRefinement.max)) {
      setGtm({
        ...gtm,
        filters: {
          ...gtm.filters,
          price: [
            {
              type: 'range',
              min: currentRefinement.min,
              max: currentRefinement.max,
            },
          ],
        },
      });

      window.dataLayer.push({
        event: 'custom.filter.price.set',
        label: 'set',
        name: 'price',
        value: {
          min: currentRefinement.min,
          max: currentRefinement.max,
        },
        newFilters: {
          ...gtm.filters,
          price: [
            {
              min: currentRefinement.min,
              max: currentRefinement.max,
            },
          ],
        },
        oldFilters: gtm.filters,
      });
    } else if (gtm.filters) {
      setGtm({
        ...gtm,
        filters: {
          ...gtm.filters,
          price: undefined,
        },
      });
    } else {
      setGtm({
        ...gtm,
      });
    }
  }, [currentRefinement.min, currentRefinement.max, currentRefinement.isRefined]);

  return (
    <PriceRangeDropdownShell
      nbHits={nbHits}
      isRefined={currentRefinement.isRefined}
      currentMin={currentRefinement.min || 0}
      currentMax={currentRefinement.max || 0}
      searchTerm={searchTerm}
    >
      {showDropdown => (
        <>
          <PriceRangeSlider
            attribute="facets.Price"
            nbHits={nbHits}
            showDropdown={showDropdown}
            setPriceRange={setPriceRange}
            setCurrentRefinement={setCurrentRefinement}
            setRangeValues={setRangeValues}
          />
          <SaleItems
            attribute="onPromotion"
            setSaleItems={setSaleItems}
            nbSaleItems={nbSaleItems}
            label="Sale items"
            value="true"
          />
        </>
      )}
    </PriceRangeDropdownShell>
  );
};

export default PriceRangeContainer;
